import { VehicleFuelType } from '@api/models/types';
import OneUpAndOneDownBarsGraph from '@components/commonGraphComponents/OneUpAndOneDownBarsGraph';
import SingleUpBarGraph from '@components/commonGraphComponents/SingleUpBarGraph';
import TwoUpAndOneDownBarsGraph from '@components/commonGraphComponents/TwoUpAndOneDownBarsGraph';
import TwoUpBarsGraph from '@components/commonGraphComponents/TwoUpBarsGraph';
import {
    convertDataToXYValue,
    mergeExistingDataWithPlaceholders,
} from '@components/commonGraphComponents/utils';
import { GraphType } from '@components/graphComponents/types';
import { Section, Values } from '@components/summary/types';
import { VehicleGraphDimensions } from '@pages/vehicleAnalysis/vehicleDataGraph';
import { IntlFormatters } from 'react-intl';

import { DateRange } from '../../../types';
import { DriverFuelType } from '../driverAnalysisReducer';
import useSelectedFuelType from '../hooks/useSelectedFuelType';

const fuelSpecificKPIS = ['coastingRating', 'brakingRating'];
const electricSpecificKPIS = ['electricRecuperationRating'];
const electricFuelConsumptionKPIS = ['drivingConsumption'];
const electricDieselCommonKPIS = [
    'averageWeight',
    'mileage',
    'fuelEfficiency',
    'foresightedDrivingPercentageV2',
    'accelerationPedalRatingV2',
    'cruiseControlRatingV2',
    'overspeedRating',
    'drivingConsumption',
];

const getGraphType = (column: Section, driverFuelType: DriverFuelType) => {
    if (driverFuelType === DriverFuelType.ALL) {
        if (electricFuelConsumptionKPIS.includes(column.key)) {
            return GraphType.TWO_UP_AND_ONE_DOWN_BARS;
        }
        if (electricDieselCommonKPIS.includes(column.key)) {
            return GraphType.TWO_UP_BARS;
        }
        if (fuelSpecificKPIS.includes(column.key)) {
            return GraphType.SINGLE_UP_BAR;
        }
        if (electricSpecificKPIS.includes(column.key)) {
            return GraphType.SINGLE_UP_BAR;
        }
    }
    if (driverFuelType === DriverFuelType.ELECTRIC) {
        if (electricFuelConsumptionKPIS.includes(column.key)) {
            return GraphType.ONE_UP_AND_ONE_DOWN_BARS;
        }
        if (
            electricDieselCommonKPIS.includes(column.key) ||
            electricSpecificKPIS.includes(column.key)
        ) {
            return GraphType.SINGLE_UP_BAR;
        }
    }
    if (driverFuelType === DriverFuelType.FUEL) {
        return GraphType.SINGLE_UP_BAR;
    }
    return GraphType.SINGLE_UP_BAR;
};

const TruEGraph = ({
    column,
    dateRange,
    dimensions,
    dieselData,
    electricData,
    intl,
    languageData,
    tickFormatter,
    selectedFuelTypes,
}: {
    column: Section;
    dateRange: DateRange;
    dimensions: VehicleGraphDimensions;
    dieselData: Values | undefined;
    electricData:
        | {
              values: Values;
              recuperationValues?: Values | undefined;
          }
        | undefined;
    intl: IntlFormatters;
    languageData: Record<string, string>;
    tickFormatter: (dateRange: DateRange) => (d: Date) => string;
    selectedFuelTypes: VehicleFuelType[];
}) => {
    const { selectedFuelType } = useSelectedFuelType();

    let selectedFuel = [];

    if (selectedFuelType === DriverFuelType.ELECTRIC || electricSpecificKPIS.includes(column.key)) {
        selectedFuel = [VehicleFuelType.ELECTRIC];
    } else if (selectedFuelType === DriverFuelType.FUEL) {
        selectedFuel = [VehicleFuelType.DIESEL];
    } else {
        selectedFuel = [VehicleFuelType.ELECTRIC, VehicleFuelType.DIESEL];
    }
    const existingData = convertDataToXYValue({
        dieselData,
        electricData,
        selectedFuelTypes: selectedFuel,
        columnName: column.key,
    });
    const mappedData = mergeExistingDataWithPlaceholders({ dateRange, existingData });

    const graphType = getGraphType(column, selectedFuelType);
    const truEGraphDimensions = { ...dimensions, horizontalMargin: 80 };

    return (
        <>
            {graphType === GraphType.SINGLE_UP_BAR && (
                <SingleUpBarGraph
                    column={column}
                    data={mappedData}
                    dateRange={dateRange}
                    dimensions={truEGraphDimensions}
                    intl={intl}
                    fuelType={selectedFuel[0]}
                    languageData={languageData}
                    tickFormatter={tickFormatter}
                />
            )}
            {graphType === GraphType.ONE_UP_AND_ONE_DOWN_BARS && (
                <OneUpAndOneDownBarsGraph
                    column={column}
                    data={mappedData}
                    dateRange={dateRange}
                    dimensions={truEGraphDimensions}
                    intl={intl}
                    languageData={languageData}
                    tickFormatter={tickFormatter}
                />
            )}
            {graphType === GraphType.TWO_UP_BARS && (
                <TwoUpBarsGraph
                    column={column}
                    data={mappedData}
                    dateRange={dateRange}
                    dimensions={truEGraphDimensions}
                    intl={intl}
                    languageData={languageData}
                    tickFormatter={tickFormatter}
                />
            )}
            {graphType === GraphType.TWO_UP_AND_ONE_DOWN_BARS && (
                <TwoUpAndOneDownBarsGraph
                    column={column}
                    data={mappedData}
                    dateRange={dateRange}
                    dimensions={truEGraphDimensions}
                    intl={intl}
                    languageData={languageData}
                    tickFormatter={tickFormatter}
                />
            )}
        </>
    );
};

export default TruEGraph;
